import { useState } from 'react';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/base/Reveal';

interface HardwareCategory {
  id: string;
  icon: string;
  name: string;
  columns: string[];
  rows: string[][];
}

interface AgriHardwareProps {
  data: {
    eyebrow: string;
    title: string;
    subtitle: string;
    categories: HardwareCategory[];
    note: string;
  };
}

export default function AgriHardware({ data }: AgriHardwareProps) {
  const [activeId, setActiveId] = useState(data.categories[0].id);
  const active = data.categories.find((c) => c.id === activeId) ?? data.categories[0];

  return (
    <section id="hardware" className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-50 scroll-mt-20">
      <div className="max-w-[1200px] mx-auto">
        <SectionHeading eyebrow={data.eyebrow} title={data.title} subtitle={data.subtitle} />

        {/* 硬件传感器产品总览图 */}
        <Reveal className="mt-10">
          <div className="rounded-2xl border border-background-200 bg-background-50 p-4 md:p-6">
            <img
              src="https://static.readdy.ai/image/4482546e014370507f3d90ff31782979/57da2fe264efa4661dddc95c1ec68ce3.png"
              alt="华辉数智智慧农业硬件传感器产品矩阵，含环境感知类、作物与病虫害感知类、控制执行类、连接与车载类共22类设备"
              title="智慧农业硬件传感器产品矩阵"
              className="w-full h-auto object-contain rounded-xl"
            />
          </div>
        </Reveal>

        <Reveal className="mt-10">
          <div className="flex flex-wrap gap-2">
            {data.categories.map((cat) => {
              const isActive = cat.id === activeId;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setActiveId(cat.id)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors border ${
                    isActive
                      ? 'bg-accent-500 text-foreground-950 border-accent-500'
                      : 'bg-background-100 text-foreground-700 border-background-200 hover:border-accent-300'
                  }`}
                >
                  <i className={`${cat.icon} text-lg`}></i>
                  {cat.name}
                </button>
              );
            })}
          </div>
        </Reveal>

        <Reveal className="mt-6" delay={80}>
          <div className="overflow-x-auto rounded-2xl border border-background-200 bg-background-100">
            <table className="w-full min-w-[720px] text-left border-collapse">
              <thead>
                <tr className="bg-background-100">
                  {active.columns.map((col) => (
                    <th
                      key={col}
                      className="px-5 py-4 text-sm font-semibold text-foreground-800 border-b border-background-200 whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {active.rows.map((row, ri) => (
                  <tr
                    key={`${active.id}-${ri}`}
                    className="border-b border-background-200 last:border-0 hover:bg-background-50/70 transition-colors"
                  >
                    {row.map((cell, ci) => (
                      <td
                        key={`${active.id}-${ri}-${ci}`}
                        className={
                          ci === 0
                            ? 'px-5 py-4 text-sm font-medium text-foreground-950 whitespace-nowrap'
                            : 'px-5 py-4 text-sm text-foreground-600'
                        }
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>

        <Reveal className="mt-4" delay={120}>
          <div className="flex items-start gap-2.5 rounded-xl bg-background-100 border border-background-200 px-4 py-3.5">
            <i className="ri-information-line text-accent-600 text-lg mt-0.5"></i>
            <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{data.note}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}